import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import {
  Package,
  Warehouse,
  Copy,
  Check,
  Sparkles,
  ExternalLink,
  Loader2,
  Trash2,
  Pencil,
  X,
  AlertTriangle,
  ImagePlus,
  TrendingUp,
  RotateCcw,
  Upload,
} from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { Modal, ModalOverlay } from 'react-aria-components';
import {
  SheetContent,
  SheetHeader,
  SheetBody,
  SheetFooter,
  SheetClose,
} from '@/components/ui/sheet';
import {
  Editable,
  EditableArea,
  EditablePreview,
  EditableInput,
} from '@/components/ui/editable';
import {
  Dialog,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ButtonLoader } from '@/components/ui/button-loader';
import { cn } from '@/lib/utils';
import { productService } from '../../services/productService';
import { brandService } from '../../services/brandService';
import { imageService } from '../../services/imageService';
import { onedriveService } from '../../services/onedriveService';
import { authService } from '../../services/authService';
import type { Product } from '../../types/domain';
import type { ProductImage } from '../../types/domain';

const getProductField = (product: Product, field: string): unknown => {
  return (product as unknown as Record<string, unknown>)[field];
};

interface ProductDetailSheetProps {
  product: Product | null;
  brands: { brand: string; count: number }[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUpdated: () => void;
}

const calculateMargin = (rate: number | null | undefined, cost: number | null | undefined) => {
  if (!rate || !cost || cost === 0) return null;
  return ((rate - cost) / cost) * 100;
};

const formatCurrency = (value?: number | null) => {
  if (value === undefined || value === null) return '—';
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
  }).format(value);
};

// Delete confirmation modal
function DeleteConfirmation({
  productName,
  productSku,
  onConfirm,
  onCancel,
}: {
  productName: string;
  productSku: string;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const [confirmText, setConfirmText] = useState('');
  const isConfirmed = confirmText.toLowerCase() === productSku.toLowerCase();

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm"
      onClick={onCancel}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 10 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 10 }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        className="bg-card border border-border rounded-xl p-6 max-w-sm w-full mx-4 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-destructive/10 flex items-center justify-center">
            <AlertTriangle size={20} className="text-destructive" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-foreground">Delete product</h3>
            <p className="text-xs text-muted-foreground">This action cannot be undone</p>
          </div>
        </div>

        <p className="text-sm text-foreground/80 mb-4">
          To confirm, type <span className="font-mono font-semibold text-foreground">{productSku}</span> below:
        </p>

        <input
          type="text"
          value={confirmText}
          onChange={(e) => setConfirmText(e.target.value)}
          placeholder={productSku}
          className="w-full px-3 py-2 rounded-lg bg-background border border-border text-sm font-mono focus:outline-none focus:ring-2 focus:ring-destructive/30 focus:border-destructive/50 mb-4"
          autoFocus
        />

        <div className="flex items-center gap-2 justify-end">
          <button
            onClick={onCancel}
            className="px-3 py-1.5 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={!isConfirmed}
            className={cn(
              'px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-200',
              isConfirmed
                ? 'bg-destructive text-white hover:bg-destructive/90'
                : 'bg-muted text-muted-foreground cursor-not-allowed'
            )}
          >
            Delete permanently
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}


export function ProductDetailSheet({
  product,
  brands,
  open,
  onOpenChange,
  onUpdated,
}: ProductDetailSheetProps) {
  const [staleProduct, setStaleProduct] = useState<Product | null>(null);
  const [dirtyFields, setDirtyFields] = useState<Record<string, unknown>>({});
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [copiedSku, setCopiedSku] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);
  const [imageError, setImageError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [currentImageUrl, setCurrentImageUrl] = useState<string | null>(null);
  const [currentLifestyleImageUrl, setCurrentLifestyleImageUrl] = useState<string | null>(null);
  const [lifestyleImageUploading, setLifestyleImageUploading] = useState(false);
  const [lifestyleImageError, setLifestyleImageError] = useState<string | null>(null);
  const [lifestyleDragOver, setLifestyleDragOver] = useState(false);
  const [productImages, setProductImages] = useState<ProductImage[]>([]);
  const [imagesLoading, setImagesLoading] = useState(false);
  const [settingPrimaryId, setSettingPrimaryId] = useState<number | null>(null);
  const [removingImageId, setRemovingImageId] = useState<number | null>(null);
  const [removeTarget, setRemoveTarget] = useState<ProductImage | null>(null);
  const [showOneDriveSearch, setShowOneDriveSearch] = useState(false);
  const [oneDriveQuery, setOneDriveQuery] = useState('');
  const [oneDriveResults, setOneDriveResults] = useState<Array<{
    id: string;
    name: string;
    size: number;
    mimeType: string | null;
    webUrl: string | null;
    createdDateTime: string | null;
    lastModifiedDateTime: string | null;
  }>>([]);
  const [oneDriveLoading, setOneDriveLoading] = useState(false);
  const [oneDriveSelected, setOneDriveSelected] = useState<Set<string>>(new Set());
  const [oneDriveImporting, setOneDriveImporting] = useState(false);
  const [oneDriveError, setOneDriveError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const lifestyleFileInputRef = useRef<HTMLInputElement>(null);
  const saveSuccessTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [registryBrands, setRegistryBrands] = useState<string[]>([]);

  useEffect(() => {
    if (product) setStaleProduct(product);
  }, [product]);

  const p = staleProduct && product && staleProduct.id === product.id
    ? staleProduct
    : (product || staleProduct);

  const hasDirtyFields = Object.keys(dirtyFields).length > 0;

  const handleFieldSubmit = useCallback((field: string, value: string) => {
    if (!p) return;
    const currentVal = String(getProductField(p, field) ?? '');
    if (value === currentVal) {
      setDirtyFields((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
      return;
    }
    setDirtyFields((prev) => ({ ...prev, [field]: value }));
  }, [p]);

  const handleNumericFieldSubmit = useCallback((field: string, value: string) => {
    if (!p) return;
    const numVal = parseFloat(value) || 0;
    const currentVal = getProductField(p, field);
    if (numVal === Number(currentVal ?? 0)) {
      setDirtyFields((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
      return;
    }
    setDirtyFields((prev) => ({ ...prev, [field]: numVal }));
  }, [p]);

  const handleSave = useCallback(async () => {
    if (!p || !hasDirtyFields) return;
    if (saveSuccessTimeoutRef.current) {
      clearTimeout(saveSuccessTimeoutRef.current);
      saveSuccessTimeoutRef.current = null;
    }
    setSaving(true);
    setSaveSuccess(false);
    setSaveError(null);
    try {
      const updatedProduct = await productService.update(p.id, dirtyFields as Partial<Product>);
      const refreshedProduct = await productService.getById(updatedProduct.id);
      setStaleProduct(refreshedProduct);
      setDirtyFields({});
      setSaveSuccess(true);
      saveSuccessTimeoutRef.current = setTimeout(() => {
        setSaveSuccess(false);
        setIsEditing(false);
        saveSuccessTimeoutRef.current = null;
      }, 1000);
      onUpdated();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to save changes';
      setSaveError(msg);
    } finally {
      setSaving(false);
    }
  }, [p, dirtyFields, hasDirtyFields, onUpdated]);

  const handleDelete = useCallback(async () => {
    if (!p) return;
    try {
      await productService.update(p.id, { status: 'inactive' });
      setShowDeleteConfirm(false);
      onOpenChange(false);
      onUpdated();
    } catch (err) {
      console.error('Error deleting product:', err);
    }
  }, [p, onOpenChange, onUpdated]);

  const handleCancelEdit = useCallback(() => {
    if (saveSuccessTimeoutRef.current) {
      clearTimeout(saveSuccessTimeoutRef.current);
      saveSuccessTimeoutRef.current = null;
    }
    setDirtyFields({});
    setSaveSuccess(false);
    setIsEditing(false);
    setSaveError(null);
  }, []);

  const copySku = useCallback(async () => {
    if (!p) return;
    await navigator.clipboard.writeText(p.sku);
    setCopiedSku(true);
    setTimeout(() => setCopiedSku(false), 1500);
  }, [p]);

  useEffect(() => {
    setDirtyFields({});
    setSaveSuccess(false);
    setSaveError(null);
    setIsEditing(false);
    setShowDeleteConfirm(false);
    setImageError(null);
    setCurrentImageUrl(product?.image_url || null);
    setCurrentLifestyleImageUrl(product?.lifestyle_image_url || null);
    setLifestyleImageError(null);
    setOneDriveQuery(product?.sku || '');
    setOneDriveResults([]);
    setOneDriveSelected(new Set());
    setOneDriveError(null);
  }, [product?.id]);

  const fetchProductImages = useCallback(async (productId?: number) => {
    if (!productId) {
      setProductImages([]);
      return;
    }
    setImagesLoading(true);
    try {
      const images = await imageService.listByProduct(productId);
      setProductImages(images);
    } catch (err) {
      console.error('Failed to load product images:', err);
      setProductImages([]);
    } finally {
      setImagesLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProductImages(product?.id);
  }, [product?.id, fetchProductImages]);

  // Canonical brand names (for the per-product "Cleaned brand" override).
  useEffect(() => {
    if (!open || registryBrands.length > 0) return;
    brandService.list()
      .then((bs) => setRegistryBrands(bs.map((b) => b.name)))
      .catch(() => {});
  }, [open, registryBrands.length]);

  useEffect(() => {
    return () => {
      if (saveSuccessTimeoutRef.current) {
        clearTimeout(saveSuccessTimeoutRef.current);
      }
    };
  }, []);

  const handleImageUpload = useCallback(async (file: File) => {
    if (!p) return;
    if (!file.type.startsWith('image/')) {
      setImageError('Please select an image file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setImageError('Image must be under 5MB');
      return;
    }

    setImageUploading(true);
    setImageError(null);
    try {
      const imageUrl = await productService.uploadImage(p.id, file);
      setCurrentImageUrl(imageUrl);
      fetchProductImages(p.id);
      onUpdated();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Upload failed';
      setImageError(msg);
    } finally {
      setImageUploading(false);
    }
  }, [p, onUpdated, fetchProductImages]);

  const handleImageDelete = useCallback(async () => {
    if (!p) return;
    setImageUploading(true);
    setImageError(null);
    try {
      await productService.deleteImage(p.id);
      setCurrentImageUrl(null);
      fetchProductImages(p.id);
      onUpdated();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to remove image';
      setImageError(msg);
    } finally {
      setImageUploading(false);
    }
  }, [p, onUpdated, fetchProductImages]);

  const handleSetPrimary = useCallback(async (image: ProductImage) => {
    if (!p || !image?.url) return;
    setSettingPrimaryId(image.id);
    try {
      await productService.update(p.id, { image_url: image.url });
      setCurrentImageUrl(image.url);
      onUpdated();
    } catch (err) {
      console.error('Failed to set primary image:', err);
    } finally {
      setSettingPrimaryId(null);
    }
  }, [p, onUpdated]);

  const handleRemoveFromProduct = useCallback(async (image: ProductImage) => {
    if (!p) return;
    setRemovingImageId(image.id);
    try {
      if (currentImageUrl && image.url === currentImageUrl) {
        await productService.update(p.id, { image_url: null });
        setCurrentImageUrl(null);
      }
      await imageService.update(image.id, { product_id: null, matched_sku: null });
      await fetchProductImages(p.id);
      onUpdated();
    } catch (err) {
      console.error('Failed to remove image association:', err);
    } finally {
      setRemovingImageId(null);
    }
  }, [p, currentImageUrl, fetchProductImages, onUpdated]);

  const handleLifestyleImageUpload = useCallback(async (file: File) => {
    if (!p) return;
    if (!file.type.startsWith('image/')) {
      setLifestyleImageError('Please select an image file');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setLifestyleImageError('Image must be under 10MB');
      return;
    }

    setLifestyleImageUploading(true);
    setLifestyleImageError(null);
    try {
      const imageUrl = await productService.uploadLifestyleImage(p.id, file);
      setCurrentLifestyleImageUrl(imageUrl);
      onUpdated();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Upload failed';
      setLifestyleImageError(msg);
    } finally {
      setLifestyleImageUploading(false);
    }
  }, [p, onUpdated]);

  const handleLifestyleImageDelete = useCallback(async () => {
    if (!p) return;
    setLifestyleImageUploading(true);
    setLifestyleImageError(null);
    try {
      await productService.deleteLifestyleImage(p.id);
      setCurrentLifestyleImageUrl(null);
      onUpdated();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to remove lifestyle image';
      setLifestyleImageError(msg);
    } finally {
      setLifestyleImageUploading(false);
    }
  }, [p, onUpdated]);

  const handleLifestyleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setLifestyleDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleLifestyleImageUpload(file);
  }, [handleLifestyleImageUpload]);

  const handleLifestyleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleLifestyleImageUpload(file);
    e.target.value = '';
  }, [handleLifestyleImageUpload]);

  const currentAgent = authService.getCachedAgent();
  const isSammie = currentAgent?.id?.toLowerCase() === 'sammie';

  const runOneDriveSearch = useCallback(async () => {
    if (!oneDriveQuery.trim()) return;
    setOneDriveLoading(true);
    setOneDriveError(null);
    try {
      const result = await onedriveService.searchImages({ query: oneDriveQuery.trim(), limit: 100 });
      setOneDriveResults(result.images || []);
      setOneDriveSelected(new Set());
      setShowOneDriveSearch(true);
    } catch (err) {
      console.error('OneDrive search failed:', err);
      setOneDriveError('Failed to search OneDrive.');
      setOneDriveResults([]);
    } finally {
      setOneDriveLoading(false);
    }
  }, [oneDriveQuery]);

  const handleOneDriveImport = useCallback(async () => {
    if (!p || oneDriveSelected.size === 0) return;
    setOneDriveImporting(true);
    setOneDriveError(null);
    try {
      const selectedItems = oneDriveResults.filter((r) => oneDriveSelected.has(r.id));
      const response = await onedriveService.importImages({
        brand: p.brand,
        product_id: p.id,
        items: selectedItems.map((r) => ({
          id: r.id,
          name: r.name,
          mimeType: r.mimeType,
          matched_sku: p.sku,
          sku_confidence: 1,
          original_filename: r.name,
        })),
      });

      if (!currentImageUrl) {
        const firstSuccess = response.results?.find((r) => r.success && r.webpUrl);
        if (firstSuccess?.webpUrl) setCurrentImageUrl(firstSuccess.webpUrl);
      }
      await fetchProductImages(p.id);
      onUpdated();
      setShowOneDriveSearch(false);
    } catch (err) {
      console.error('OneDrive import failed:', err);
      setOneDriveError('Failed to import OneDrive images.');
    } finally {
      setOneDriveImporting(false);
    }
  }, [p, oneDriveSelected, oneDriveResults, currentImageUrl, fetchProductImages, onUpdated]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleImageUpload(file);
  }, [handleImageUpload]);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleImageUpload(file);
    // Reset so same file can be selected again
    e.target.value = '';
  }, [handleImageUpload]);

  const effectiveRate = useMemo(() => {
    if (!p) return null;
    return dirtyFields.rate !== undefined ? Number(dirtyFields.rate) : p.rate;
  }, [p, dirtyFields.rate]);

  const effectiveName = useMemo(() => {
    if (!p) return '';
    return dirtyFields.name !== undefined ? String(dirtyFields.name) : p.name;
  }, [p, dirtyFields.name]);

  const effectiveCost = useMemo(() => {
    if (!p) return null;
    return dirtyFields.cost_price !== undefined ? Number(dirtyFields.cost_price) : p.cost_price;
  }, [p, dirtyFields.cost_price]);

  const margin = calculateMargin(effectiveRate, effectiveCost);
  const displayDescription = p?.ai_description || p?.description;
  const isAiDescription = !!p?.ai_description;

  const pk = p?.id ?? 0;

  const stockColor = !p
    ? ''
    : p.stock_on_hand === 0
      ? 'text-red-400'
      : p.stock_on_hand <= 10
        ? 'text-amber-400'
        : 'text-emerald-400';

  const stockBg = !p
    ? ''
    : p.stock_on_hand === 0
      ? 'bg-red-500/10 border-red-500/20'
      : p.stock_on_hand <= 10
        ? 'bg-amber-500/10 border-amber-500/20'
        : 'bg-emerald-500/10 border-emerald-500/20';

  const stockLabel = !p
    ? ''
    : p.stock_on_hand === 0
      ? 'Out of stock'
      : p.stock_on_hand <= 10
        ? 'Low stock'
        : 'In stock';

  return (
    <>
      <SheetContent
        isOpen={open}
        onOpenChange={onOpenChange}
        side="right"
        isFloat={false}
        isDismissable={!showDeleteConfirm && !removeTarget}
        className="sm:max-w-[720px] w-full backdrop-blur-xl bg-card/95"
        aria-label="Product details"
      >
        {p && (
          <>
            {/* Header - Hero zone */}
            <SheetHeader className="border-b border-border/60 px-5 py-4">
              <div className="flex gap-4 pr-8">
                {/* Product Image / Upload Dropzone */}
                <div className="shrink-0 relative">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                  {currentImageUrl ? (
                    <div className="relative group">
                      <motion.img
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        src={currentImageUrl}
                        alt={effectiveName}
                        className="w-20 h-20 object-contain rounded-xl border border-border bg-background"
                      />
                      {/* Loading overlay */}
                      {imageUploading && (
                        <div className="absolute inset-0 rounded-xl bg-black/60 flex items-center justify-center">
                          <Loader2 size={18} className="text-white animate-spin" />
                        </div>
                      )}
                      {/* Hover overlay with replace/delete */}
                      {!imageUploading && (
                        <div className="absolute inset-0 rounded-xl bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1.5">
                          <button
                            onClick={() => fileInputRef.current?.click()}
                            className="p-1.5 rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
                            title="Replace image"
                          >
                            <Upload size={12} className="text-white" />
                          </button>
                          <button
                            onClick={handleImageDelete}
                            className="p-1.5 rounded-lg bg-white/10 hover:bg-red-500/40 transition-colors"
                            title="Remove image"
                          >
                            <Trash2 size={12} className="text-white" />
                          </button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div
                      onClick={() => fileInputRef.current?.click()}
                      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                      onDragLeave={() => setDragOver(false)}
                      onDrop={handleDrop}
                      className={cn(
                        'w-20 h-20 rounded-xl border border-dashed bg-muted/30 flex flex-col items-center justify-center gap-1 cursor-pointer transition-all group',
                        dragOver
                          ? 'border-primary bg-primary/10 scale-105'
                          : 'border-border/60 hover:border-primary/30 hover:bg-primary/5'
                      )}
                    >
                      {imageUploading ? (
                        <Loader2 size={18} className="text-primary animate-spin" />
                      ) : (
                        <>
                          <ImagePlus size={18} className="text-muted-foreground/30 group-hover:text-primary/50" />
                          <span className="text-[9px] text-muted-foreground/40 group-hover:text-primary/50">
                            {dragOver ? 'Drop here' : 'Add image'}
                          </span>
                        </>
                      )}
                    </div>
                  )}
                  {imageError && (
                    <p className="absolute -bottom-5 left-0 text-[9px] text-destructive whitespace-nowrap">{imageError}</p>
                  )}
                </div>

                {/* Product identity */}
                <div className="flex-1 min-w-0">
                  <h2 className="text-lg font-semibold text-foreground leading-tight mb-2 pr-4">
                    {effectiveName}
                  </h2>

                  <div className="flex flex-wrap items-center gap-1.5">
                    <button
                      onClick={copySku}
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-muted/50 border border-border/50 text-[11px] font-mono text-muted-foreground hover:border-primary/30 hover:text-foreground transition-all"
                      title="Copy SKU"
                    >
                      {copiedSku ? <Check size={9} className="text-emerald-400" /> : <Copy size={9} />}
                      {p.sku}
                    </button>
                    {p.brand && (
                      <span className="px-2 py-0.5 rounded-md bg-primary/8 border border-primary/15 text-[11px] font-medium text-primary">
                        {p.brand}
                      </span>
                    )}
                    {isSammie && p.sku && (
                      <button
                        onClick={() => {
                          setOneDriveQuery(p.sku || '');
                          setShowOneDriveSearch((prev) => !prev);
                          setTimeout(() => runOneDriveSearch(), 0);
                        }}
                        className="px-2 py-0.5 rounded-md bg-sky-500/10 border border-sky-500/20 text-[11px] font-medium text-sky-300 hover:bg-sky-500/20 transition-colors"
                        type="button"
                      >
                        {showOneDriveSearch ? 'Hide OneDrive' : 'Search OneDrive'}
                      </button>
                    )}
                    <span
                      className={cn(
                        'px-2 py-0.5 rounded-md text-[11px] font-medium border',
                        p.status === 'active'
                          ? 'bg-emerald-500/8 text-emerald-400 border-emerald-500/15'
                          : 'bg-muted text-muted-foreground border-border'
                      )}
                    >
                      {p.status}
                    </span>
                  </div>
                </div>
              </div>
            </SheetHeader>

            <SheetBody className="px-5 py-4 space-y-4 overflow-y-auto">
              {/* Available Stock — per-location from Zoho */}
              {(() => {
                const visibleLocations = (p.locations ?? []).filter(
                  (l) => l && l.location_name && (l.status ?? 'active') === 'active',
                );
                if (visibleLocations.length === 0) return null;
                const asNum = (v: number | string | null | undefined) => {
                  if (v === null || v === undefined || v === '') return 0;
                  return typeof v === 'number' ? v : Number(v) || 0;
                };
                return (
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.02 }}
                    className="rounded-xl border border-border/40 p-4 space-y-3"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Warehouse size={14} className="text-muted-foreground/60" />
                        <h3 className="text-xs font-medium text-muted-foreground/60 uppercase tracking-wider">
                          Available Stock
                        </h3>
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {visibleLocations.length} location{visibleLocations.length === 1 ? '' : 's'}
                      </span>
                    </div>

                    <div className="overflow-hidden rounded-lg border border-border/40">
                      <div className="grid grid-cols-[minmax(0,1fr)_70px_70px_90px] gap-0 bg-muted/30 px-3 py-2 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                        <div>Location</div>
                        <div className="text-right">On Hand</div>
                        <div className="text-right">Available</div>
                        <div className="text-right">Actual Avail.</div>
                      </div>
                      {visibleLocations.map((loc, idx) => {
                        const onHand = asNum(loc.location_stock_on_hand);
                        const tone =
                          onHand === 0
                            ? 'text-red-400'
                            : onHand <= 10
                              ? 'text-amber-400'
                              : 'text-emerald-400';
                        return (
                          <div
                            key={loc.location_id || `${loc.location_name}-${idx}`}
                            className={cn(
                              'grid grid-cols-[minmax(0,1fr)_70px_70px_90px] items-center px-3 py-2 text-sm',
                              idx !== visibleLocations.length - 1 && 'border-b border-border/40',
                            )}
                          >
                            <div className="flex items-center gap-2 min-w-0">
                              <span className="truncate">{loc.location_name}</span>
                              {loc.is_primary && (
                                <span className="shrink-0 rounded-full border border-primary/30 bg-primary/10 px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-wider text-primary">
                                  Primary
                                </span>
                              )}
                            </div>
                            <div className={cn('text-right tabular-nums font-medium', tone)}>
                              {onHand}
                            </div>
                            <div className="text-right tabular-nums text-muted-foreground">
                              {asNum(loc.location_available_stock)}
                            </div>
                            <div className="text-right tabular-nums text-muted-foreground">
                              {asNum(loc.location_actual_available_stock)}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </motion.div>
                );
              })()}

              {/* Product Images */}
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.04 }}
                className="rounded-xl border border-border/40 p-4 space-y-3"
              >
                <div className="flex items-center justify-between">
                  <h3 className="text-xs font-medium text-muted-foreground/60 uppercase tracking-wider">
                    Product Images
                  </h3>
                  <span className="text-xs text-muted-foreground">
                    {imagesLoading ? 'Loading…' : `${productImages.length} image${productImages.length === 1 ? '' : 's'}`}
                  </span>
                </div>

                {imagesLoading ? (
                  <div className="grid grid-cols-4 gap-2 max-md:grid-cols-2">
                    {Array.from({ length: 4 }).map((_, i) => (
                      <div key={i} className="h-20 rounded-lg bg-muted/40 animate-pulse" />
                    ))}
                  </div>
                ) : productImages.length === 0 ? (
                  <div className="text-sm text-muted-foreground">No images linked to this product yet.</div>
                ) : (
                  <div className="grid grid-cols-4 gap-3 max-md:grid-cols-2">
                    {productImages.map((img) => {
                      const isPrimary = !!currentImageUrl && img.url === currentImageUrl;
                      return (
                        <div
                          key={img.id}
                          className={cn(
                            'group relative rounded-lg border border-border/40 bg-muted/10 overflow-hidden',
                            isPrimary && 'ring-1 ring-primary/40'
                          )}
                        >
                          <img
                            src={img.url}
                            alt={img.filename}
                            className="w-full h-20 object-contain bg-background"
                          />
                          <div className="absolute inset-x-0 bottom-0 flex items-center justify-between gap-2 px-2 py-1 bg-black/55 text-white text-[10px]">
                            <span className="truncate">{img.filename}</span>
                            {isPrimary && (
                              <span className="px-1.5 py-0.5 rounded bg-primary/80 text-[9px] uppercase tracking-wider">
                                Primary
                              </span>
                            )}
                          </div>
                          <div className="absolute top-1 right-1 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            {!isPrimary && (
                              <button
                                onClick={() => handleSetPrimary(img)}
                                disabled={settingPrimaryId === img.id}
                                className="px-2 py-0.5 rounded bg-white/90 text-[10px] text-black shadow-sm"
                              >
                                {settingPrimaryId === img.id ? 'Setting…' : 'Set primary'}
                              </button>
                            )}
                            <button
                              onClick={() => setRemoveTarget(img)}
                              disabled={removingImageId === img.id}
                              className="px-2 py-0.5 rounded bg-red-500/90 text-[10px] text-white shadow-sm"
                            >
                              {removingImageId === img.id ? 'Removing…' : 'Remove'}
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </motion.div>

              {/* Lifestyle Image (Brand Explore / Showcase) */}
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.045 }}
                className="rounded-xl border border-border/40 p-4 space-y-3"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <h3 className="text-xs font-medium text-muted-foreground/60 uppercase tracking-wider">
                      Lifestyle Image
                    </h3>
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-medium bg-teal-500/10 text-teal-400 border border-teal-500/20">
                      Brand Explore
                    </span>
                  </div>
                  {currentLifestyleImageUrl ? (
                    <span className="text-[11px] text-emerald-400 font-medium">Configured</span>
                  ) : (
                    <span className="text-[11px] text-muted-foreground">Not set</span>
                  )}
                </div>

                <input
                  ref={lifestyleFileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleLifestyleFileSelect}
                  className="hidden"
                />

                {currentLifestyleImageUrl ? (
                  <div className="space-y-3">
                    <div className="relative group rounded-xl overflow-hidden border border-border/60 bg-muted/20 aspect-[16/9] max-h-52 w-full flex items-center justify-center">
                      <img
                        src={currentLifestyleImageUrl}
                        alt={p.lifestyle_image_alt || effectiveName}
                        className="w-full h-full object-cover"
                      />
                      {lifestyleImageUploading && (
                        <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                          <Loader2 size={24} className="text-white animate-spin" />
                        </div>
                      )}
                      {!lifestyleImageUploading && (
                        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                          <button
                            onClick={() => lifestyleFileInputRef.current?.click()}
                            className="px-2.5 py-1.5 rounded-lg bg-white/20 hover:bg-white/30 text-white text-xs font-medium flex items-center gap-1.5 transition-colors"
                            title="Replace lifestyle image"
                          >
                            <Upload size={13} />
                            Replace
                          </button>
                          <button
                            onClick={handleLifestyleImageDelete}
                            className="px-2.5 py-1.5 rounded-lg bg-red-500/70 hover:bg-red-500 text-white text-xs font-medium flex items-center gap-1.5 transition-colors"
                            title="Remove lifestyle image"
                          >
                            <Trash2 size={13} />
                            Remove
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Alt text display / inline edit */}
                    <div className="space-y-1.5 rounded-lg bg-muted/20 border border-border/30 p-2.5 text-xs">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-muted-foreground font-medium shrink-0">Alt text:</span>
                        {isEditing ? (
                          <Editable
                            key={`lifestyle_alt-${pk}`}
                            defaultValue={p.lifestyle_image_alt || ''}
                            placeholder="Add image alt description..."
                            onSubmit={(val) => handleFieldSubmit('lifestyle_image_alt', val)}
                          >
                            <EditableArea className="flex-1 text-right">
                              <EditablePreview className="text-xs text-foreground py-0 text-right truncate" />
                              <EditableInput className="text-xs px-1 text-right" />
                            </EditableArea>
                          </Editable>
                        ) : (
                          <span className="text-foreground text-right truncate">
                            {p.lifestyle_image_alt || <span className="text-muted-foreground italic">Falls back to product name</span>}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div
                    onClick={() => lifestyleFileInputRef.current?.click()}
                    onDragOver={(e) => { e.preventDefault(); setLifestyleDragOver(true); }}
                    onDragLeave={() => setLifestyleDragOver(false)}
                    onDrop={handleLifestyleDrop}
                    className={cn(
                      'rounded-xl border border-dashed bg-muted/20 py-6 px-4 flex flex-col items-center justify-center gap-2 cursor-pointer transition-all group',
                      lifestyleDragOver
                        ? 'border-primary bg-primary/10 scale-[1.01]'
                        : 'border-border/60 hover:border-primary/40 hover:bg-primary/5'
                    )}
                  >
                    {lifestyleImageUploading ? (
                      <Loader2 size={22} className="text-primary animate-spin" />
                    ) : (
                      <>
                        <div className="w-10 h-10 rounded-full bg-muted/50 flex items-center justify-center group-hover:bg-primary/10 transition-colors">
                          <ImagePlus size={20} className="text-muted-foreground group-hover:text-primary transition-colors" />
                        </div>
                        <div className="text-center">
                          <p className="text-xs font-medium text-foreground group-hover:text-primary transition-colors">
                            {lifestyleDragOver ? 'Drop lifestyle image here' : 'Upload lifestyle photography'}
                          </p>
                          <p className="text-[11px] text-muted-foreground mt-0.5">
                            Room/environmental photography used in brand explore showcase
                          </p>
                        </div>
                      </>
                    )}
                  </div>
                )}
                {lifestyleImageError && (
                  <p className="text-xs text-destructive mt-1">{lifestyleImageError}</p>
                )}

                {/* In edit mode, allow manual URL entry as well */}
                {isEditing && (
                  <div className="pt-2 border-t border-border/30 space-y-2">
                    <DetailRow label="Lifestyle URL" editing={isEditing}>
                      <Editable
                        key={`lifestyle_image_url-${pk}`}
                        defaultValue={currentLifestyleImageUrl || ''}
                        placeholder="https://..."
                        onSubmit={(val) => {
                          handleFieldSubmit('lifestyle_image_url', val);
                          setCurrentLifestyleImageUrl(val || null);
                        }}
                      >
                        <EditableArea>
                          <EditablePreview className="text-xs text-foreground py-0 truncate max-w-[300px]" />
                          <EditableInput className="text-xs px-1" />
                        </EditableArea>
                      </Editable>
                    </DetailRow>
                    {!currentLifestyleImageUrl && (
                      <DetailRow label="Lifestyle Alt" editing={isEditing}>
                        <Editable
                          key={`lifestyle_image_alt-${pk}`}
                          defaultValue={p.lifestyle_image_alt || ''}
                          placeholder="Image description for alt tag"
                          onSubmit={(val) => handleFieldSubmit('lifestyle_image_alt', val)}
                        >
                          <EditableArea>
                            <EditablePreview className="text-xs text-foreground py-0" />
                            <EditableInput className="text-xs px-1" />
                          </EditableArea>
                        </Editable>
                      </DetailRow>
                    )}
                  </div>
                )}
              </motion.div>

              {/* OneDrive Search Accordion */}
              {isSammie && (
                <motion.div
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.035 }}
                  className="rounded-xl border border-border/40 p-4 space-y-3"
                >
                  <button
                    className="flex w-full items-center justify-between text-xs font-medium text-muted-foreground/70 uppercase tracking-wider"
                    onClick={() => setShowOneDriveSearch((prev) => !prev)}
                    type="button"
                  >
                    <span>OneDrive Search</span>
                    <span className="text-[10px]">{showOneDriveSearch ? '−' : '+'}</span>
                  </button>

                  {showOneDriveSearch && (
                    <div className="space-y-3">
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={oneDriveQuery}
                          onChange={(e) => setOneDriveQuery(e.target.value)}
                          placeholder="Search OneDrive..."
                          className="flex-1 h-9 px-3 rounded-lg bg-background border border-border text-sm"
                        />
                        <Button intent="outline" size="sm" onPress={runOneDriveSearch} isDisabled={oneDriveLoading || !oneDriveQuery.trim()}>
                          {oneDriveLoading ? 'Searching…' : 'Search'}
                        </Button>
                      </div>

                      {oneDriveError && (
                        <div className="text-sm text-red-400">{oneDriveError}</div>
                      )}

                      {oneDriveLoading ? (
                        <div className="text-sm text-muted-foreground">Searching OneDrive…</div>
                      ) : oneDriveResults.length === 0 ? (
                        <div className="text-sm text-muted-foreground">No images found.</div>
                      ) : (
                        <div className="border border-border/60 rounded-lg overflow-hidden">
                          <div className="grid grid-cols-[36px_1fr_100px] gap-2 px-3 py-2 text-xs uppercase tracking-wider text-muted-foreground bg-muted/30">
                            <span />
                            <span>File</span>
                            <span>Size</span>
                          </div>
                          <div className="max-h-[260px] overflow-y-auto divide-y divide-border/40">
                            {oneDriveResults.map((item) => (
                              <label key={item.id} className="grid grid-cols-[36px_1fr_100px] gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-muted/20">
                                <input
                                  type="checkbox"
                                  checked={oneDriveSelected.has(item.id)}
                                  onChange={() => {
                                    setOneDriveSelected((prev) => {
                                      const next = new Set(prev);
                                      if (next.has(item.id)) next.delete(item.id);
                                      else next.add(item.id);
                                      return next;
                                    });
                                  }}
                                />
                                <span className="truncate">{item.name}</span>
                                <span className="text-xs text-muted-foreground">{(item.size / 1024).toFixed(1)} KB</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      )}

                      <div className="flex justify-end gap-2">
                        <Button intent="outline" size="sm" onPress={() => setShowOneDriveSearch(false)}>
                          Close
                        </Button>
                        <Button
                          intent="primary"
                          size="sm"
                          onPress={handleOneDriveImport}
                          isDisabled={oneDriveImporting || oneDriveSelected.size === 0}
                        >
                          {oneDriveImporting ? 'Importing…' : `Import ${oneDriveSelected.size || ''}`.trim()}
                        </Button>
                      </div>
                    </div>
                  )}
                </motion.div>
              )}

              {/* Product Details — always visible */}
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.05 }}
                className="rounded-xl border border-border/40 divide-y divide-border/30"
              >
                <DetailRow label="Name" editing={isEditing}>
                  {isEditing ? (
                    <Editable
                      key={`name-${pk}`}
                      defaultValue={p.name}
                      onSubmit={(val) => handleFieldSubmit('name', val)}
                    >
                      <EditableArea>
                        <EditablePreview className="text-sm text-foreground py-0" />
                        <EditableInput className="text-sm px-1" />
                      </EditableArea>
                    </Editable>
                  ) : (
                    <span className="text-sm text-foreground">{effectiveName}</span>
                  )}
                </DetailRow>

                <DetailRow label="SKU" editing={isEditing}>
                  {isEditing ? (
                    <Editable
                      key={`sku-${pk}`}
                      defaultValue={p.sku}
                      onSubmit={(val) => handleFieldSubmit('sku', val)}
                    >
                      <EditableArea>
                        <EditablePreview className="text-sm text-foreground py-0 font-mono" />
                        <EditableInput className="text-sm font-mono px-1" />
                      </EditableArea>
                    </Editable>
                  ) : (
                    <span className="text-sm text-foreground font-mono">{p.sku}</span>
                  )}
                </DetailRow>

                {p.ean && (
                  <DetailRow label="EAN" editing={isEditing}>
                    {isEditing ? (
                      <Editable
                        key={`ean-${pk}`}
                        defaultValue={p.ean || ''}
                        placeholder="—"
                        onSubmit={(val) => handleFieldSubmit('ean', val)}
                      >
                        <EditableArea>
                          <EditablePreview className="text-sm text-foreground py-0 font-mono" />
                          <EditableInput className="text-sm font-mono px-1" />
                        </EditableArea>
                      </Editable>
                    ) : (
                      <span className="text-sm text-foreground font-mono">{p.ean}</span>
                    )}
                  </DetailRow>
                )}

                <DetailRow label="Brand" editing={isEditing}>
                  {isEditing ? (
                    <Editable
                      key={`brand-${pk}`}
                      defaultValue={p.brand}
                      onSubmit={(val) => handleFieldSubmit('brand', val)}
                    >
                      <EditableArea>
                        <EditablePreview className="text-sm text-foreground py-0" />
                        <EditableInput className="text-sm px-1" />
                      </EditableArea>
                    </Editable>
                  ) : (
                    <span className="text-sm text-foreground">{p.brand}</span>
                  )}
                </DetailRow>

                <DetailRow label="Cleaned brand" editing={isEditing}>
                  {isEditing ? (
                    <select
                      value={
                        dirtyFields.brand_name_cleaned !== undefined
                          ? String(dirtyFields.brand_name_cleaned)
                          : dirtyFields.brand_cleaned_locked === false
                            ? ''
                            : p.brand_cleaned_locked
                              ? (p.brand_name_cleaned ?? '')
                              : ''
                      }
                      onChange={(e) => {
                        const val = e.target.value;
                        const currentLocked = !!p.brand_cleaned_locked;
                        const currentClean = p.brand_name_cleaned ?? '';
                        setDirtyFields((prev) => {
                          const next = { ...prev };
                          if (val !== '') {
                            if (val === currentClean && currentLocked) {
                              delete next.brand_name_cleaned;
                              delete next.brand_cleaned_locked;
                            } else {
                              next.brand_name_cleaned = val;
                              next.brand_cleaned_locked = true;
                            }
                          } else if (!currentLocked) {
                            delete next.brand_name_cleaned;
                            delete next.brand_cleaned_locked;
                          } else {
                            next.brand_cleaned_locked = false;
                            delete next.brand_name_cleaned;
                          }
                          return next;
                        });
                      }}
                      className="h-8 rounded-md border border-input bg-background px-2 text-sm"
                    >
                      <option value="">Auto (from brand mapping)</option>
                      {registryBrands.map((name) => (
                        <option key={name} value={name}>{name}</option>
                      ))}
                    </select>
                  ) : (
                    <span className="text-sm text-foreground">
                      {p.brand_name_cleaned || '—'}
                      {p.brand_cleaned_locked && (
                        <span className="ml-2 align-middle text-[10px] text-muted-foreground">(manual override)</span>
                      )}
                    </span>
                  )}
                </DetailRow>

                <DetailRow label="Category" editing={false}>
                  <span className="text-sm text-foreground">{p.category_name || '—'}</span>
                </DetailRow>

                <DetailRow label="Pack Qty" editing={isEditing}>
                  {isEditing ? (
                    <Editable
                      key={`pack_qty-${pk}`}
                      defaultValue={String(p.pack_qty ?? '')}
                      placeholder="—"
                      onSubmit={(val) => handleNumericFieldSubmit('pack_qty', val)}
                    >
                      <EditableArea>
                        <EditablePreview className="text-sm text-foreground py-0" />
                        <EditableInput className="text-sm px-1" type="number" />
                      </EditableArea>
                    </Editable>
                  ) : (
                    <span className="text-sm text-foreground">{p.pack_qty ?? '—'}</span>
                  )}
                </DetailRow>

                {p.dimensions_formatted && (
                  <DetailRow label="Dimensions" editing={false}>
                    <span className="text-sm text-foreground">{p.dimensions_formatted}</span>
                  </DetailRow>
                )}

                {(p.materials || isEditing) && (
                  <DetailRow label="Materials" editing={isEditing}>
                    {isEditing ? (
                      <Editable
                        key={`materials-${pk}`}
                        defaultValue={p.materials || ''}
                        placeholder="—"
                        onSubmit={(val) => handleFieldSubmit('materials', val)}
                      >
                        <EditableArea>
                          <EditablePreview className="text-sm text-foreground py-0" />
                          <EditableInput className="text-sm px-1" />
                        </EditableArea>
                      </Editable>
                    ) : (
                      <span className="text-sm text-foreground">{p.materials}</span>
                    )}
                  </DetailRow>
                )}

                {(p.color_family || isEditing) && (
                  <DetailRow label="Color" editing={isEditing}>
                    {isEditing ? (
                      <Editable
                        key={`color-${pk}`}
                        defaultValue={p.color_family || ''}
                        placeholder="—"
                        onSubmit={(val) => handleFieldSubmit('color_family', val)}
                      >
                        <EditableArea>
                          <EditablePreview className="text-sm text-foreground py-0" />
                          <EditableInput className="text-sm px-1" />
                        </EditableArea>
                      </Editable>
                    ) : (
                      <span className="text-sm text-foreground">{p.color_family}</span>
                    )}
                  </DetailRow>
                )}
              </motion.div>

              {/* Pricing + Stock */}
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="rounded-xl border border-border/40 divide-y divide-border/30"
              >
                <DetailRow label="Cost" editing={isEditing}>
                  {isEditing ? (
                    <Editable
                      key={`cost_price-${pk}`}
                      defaultValue={String(p.cost_price ?? '')}
                      placeholder="—"
                      onSubmit={(val) => handleNumericFieldSubmit('cost_price', val)}
                    >
                      <EditableArea>
                        <EditablePreview className="text-sm font-medium text-foreground/70 tabular-nums py-0" />
                        <EditableInput className="text-sm font-medium px-1 w-20 text-right" type="number" />
                      </EditableArea>
                    </Editable>
                  ) : (
                    <span className="text-sm font-medium text-foreground/70 tabular-nums">
                      {formatCurrency(effectiveCost)}
                    </span>
                  )}
                </DetailRow>

                <DetailRow label="Rate" editing={isEditing}>
                  {isEditing ? (
                    <Editable
                      key={`rate-${pk}`}
                      defaultValue={String(p.rate ?? '')}
                      placeholder="—"
                      onSubmit={(val) => handleNumericFieldSubmit('rate', val)}
                    >
                      <EditableArea>
                        <EditablePreview className="text-sm font-semibold text-foreground tabular-nums py-0" />
                        <EditableInput className="text-sm font-semibold px-1 w-20 text-right" type="number" />
                      </EditableArea>
                    </Editable>
                  ) : (
                    <span className="text-sm font-semibold text-foreground tabular-nums">
                      {formatCurrency(effectiveRate)}
                    </span>
                  )}
                </DetailRow>

                <DetailRow label="Margin" editing={false}>
                  <span className={cn(
                    'text-sm font-semibold tabular-nums',
                    !margin ? 'text-muted-foreground'
                      : margin >= 40 ? 'text-emerald-400'
                      : margin >= 20 ? 'text-primary'
                      : margin >= 0 ? 'text-amber-400'
                      : 'text-red-400'
                  )}>
                    {margin !== null ? `${Math.round(margin)}%` : '—'}
                  </span>
                </DetailRow>

                <div className={cn(
                  'flex items-center justify-between px-3 py-2.5',
                  stockBg.replace('rounded-xl border', '')
                )}>
                  <span className="text-xs text-muted-foreground/60 w-24 shrink-0">Stock</span>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2">
                      <div className={cn(
                        'w-1.5 h-1.5 rounded-full',
                        p.stock_on_hand === 0 ? 'bg-red-400' : p.stock_on_hand <= 10 ? 'bg-amber-400' : 'bg-emerald-400'
                      )} />
                      <span className={cn('text-sm font-semibold tabular-nums', stockColor)}>
                        {p.stock_on_hand.toLocaleString()}
                      </span>
                      {p.unit && <span className="text-xs text-muted-foreground">{p.unit}</span>}
                    </div>
                    <span className={cn('text-[11px] font-medium', stockColor)}>
                      {stockLabel}
                    </span>
                  </div>
                </div>
              </motion.div>

              {/* Description */}
              {(displayDescription || isEditing) && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.15 }}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="text-xs font-medium text-muted-foreground/60 uppercase tracking-wider">
                      Description
                    </h3>
                    {isAiDescription && (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-medium bg-primary/10 text-primary border border-primary/15">
                        <Sparkles size={9} />
                        AI Generated
                      </span>
                    )}
                  </div>
                  {isEditing ? (
                    <Editable
                      key={`desc-${pk}`}
                      defaultValue={displayDescription || ''}
                      placeholder="No description. Click to add..."
                      onSubmit={(val) => {
                        const field = isAiDescription ? 'ai_description' : 'description';
                        handleFieldSubmit(field, val);
                      }}
                    >
                      <EditableArea>
                        <EditablePreview className="text-sm text-foreground/70 leading-relaxed py-0.5" />
                        <EditableInput
                          asChild
                          className="text-sm leading-relaxed min-h-[80px] px-3 py-2 rounded-lg"
                        >
                          <textarea rows={4} />
                        </EditableInput>
                      </EditableArea>
                    </Editable>
                  ) : (
                    <p className="text-sm text-foreground/70 leading-relaxed">
                      {displayDescription}
                    </p>
                  )}
                </motion.div>
              )}

              {/* AI Features */}
              {p.ai_features && p.ai_features.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                >
                  <h3 className="text-xs font-medium text-muted-foreground/60 uppercase tracking-wider mb-2 flex items-center gap-2">
                    Features
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-medium bg-primary/10 text-primary border border-primary/15">
                      <Sparkles size={9} />
                      AI
                    </span>
                  </h3>
                  <div className="flex flex-wrap gap-1.5">
                    {p.ai_features.map((feature, i) => (
                      <span
                        key={i}
                        className="px-2.5 py-1 rounded-lg bg-muted/40 border border-border/40 text-xs text-foreground/70"
                      >
                        {feature}
                      </span>
                    ))}
                  </div>
                </motion.div>
              )}

              {/* Category Path */}
              {(p.category_l1 || p.category_l2 || p.category_l3) && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.25 }}
                >
                  <h3 className="text-xs font-medium text-muted-foreground/60 uppercase tracking-wider mb-2">
                    Category Path
                  </h3>
                  <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                    {p.category_l1 && <span className="px-2 py-0.5 rounded bg-muted/30 text-xs">{p.category_l1}</span>}
                    {p.category_l2 && (
                      <>
                        <span className="text-muted-foreground/20">/</span>
                        <span className="px-2 py-0.5 rounded bg-muted/30 text-xs">{p.category_l2}</span>
                      </>
                    )}
                    {p.category_l3 && (
                      <>
                        <span className="text-muted-foreground/20">/</span>
                        <span className="px-2 py-0.5 rounded bg-muted/30 text-xs">{p.category_l3}</span>
                      </>
                    )}
                  </div>
                </motion.div>
              )}

              {/* Image URL (editing only) */}
              {currentImageUrl && isEditing && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.28 }}
                  className="rounded-xl border border-border/40 divide-y divide-border/30"
                >
                  <DetailRow label="Image URL" editing={isEditing}>
                    <Editable
                      key={`image_url-${pk}`}
                      defaultValue={currentImageUrl || ''}
                      placeholder="—"
                      onSubmit={(val) => handleFieldSubmit('image_url', val)}
                    >
                      <EditableArea>
                        <EditablePreview className="text-sm text-foreground py-0 truncate max-w-[300px]" />
                        <EditableInput className="text-sm px-1" />
                      </EditableArea>
                    </Editable>
                  </DetailRow>
                </motion.div>
              )}

              {/* Integration */}
              {p.zoho_item_id && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="border-t border-border/40 pt-4"
                >
                  <h3 className="text-xs font-medium text-muted-foreground/60 uppercase tracking-wider mb-2">
                    Integration
                  </h3>
                  <div className="flex items-center justify-between">
                    <a
                      href={`https://inventory.zoho.eu/app#/items/${p.zoho_item_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-primary transition-colors"
                    >
                      <ExternalLink size={12} />
                      View in Zoho
                    </a>
                    {p.sync_status && (
                      <span
                        className={cn(
                          'text-[10px] px-2 py-0.5 rounded-md border font-medium',
                          p.sync_status === 'synced'
                            ? 'bg-emerald-500/8 text-emerald-400 border-emerald-500/15'
                            : p.sync_status === 'pending_push'
                              ? 'bg-amber-500/8 text-amber-400 border-amber-500/15'
                              : 'bg-red-500/8 text-red-400 border-red-500/15'
                        )}
                      >
                        {p.sync_status === 'synced' ? 'Synced' : p.sync_status === 'pending_push' ? 'Pending' : 'Conflict'}
                      </span>
                    )}
                  </div>
                  {p.sync_status === 'pending_push' && (
                    <p className="text-[11px] text-amber-400/70 mt-1">Changes pending sync to Zoho</p>
                  )}
                </motion.div>
              )}
            </SheetBody>

            {/* Footer */}
            <SheetFooter className="border-t border-border/60 px-5 py-3">
              <div className="flex items-center justify-between w-full">
                {/* Left: Delete */}
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="inline-flex items-center gap-1.5 text-xs text-muted-foreground/50 hover:text-destructive transition-colors"
                >
                  <Trash2 size={13} />
                  Delete
                </button>

                {/* Right: Edit / Save / Close */}
                <div className="flex items-center gap-2">
                  {saveError && (
                    <span className="text-xs text-destructive mr-2">{saveError}</span>
                  )}

                  {isEditing ? (
                    <>
                      <Button intent="outline" size="sm" onPress={handleCancelEdit}>
                        <X size={13} className="mr-1" />
                        Cancel
                      </Button>
                      {(hasDirtyFields || saveSuccess) && (
                        <Button
                          intent="primary"
                          size="sm"
                          onPress={handleSave}
                          isDisabled={saving || saveSuccess}
                        >
                          {saving ? (
                            <>
                              <ButtonLoader className="mr-1" />
                              Saving...
                            </>
                          ) : saveSuccess ? (
                            <>
                              <Check size={13} className="mr-1" />
                              Saved
                            </>
                          ) : (
                            'Save Changes'
                          )}
                        </Button>
                      )}
                    </>
                  ) : (
                    <>
                      <SheetClose intent="outline" size="sm">
                        Close
                      </SheetClose>
                      <Button intent="primary" size="sm" onPress={() => setIsEditing(true)}>
                        <Pencil size={13} className="mr-1" />
                        Edit
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </SheetFooter>
          </>
        )}
      </SheetContent>

      {/* Delete Confirmation Portal */}
      <AnimatePresence>
        {showDeleteConfirm && p && (
          <DeleteConfirmation
            productName={p.name}
            productSku={p.sku}
            onConfirm={handleDelete}
            onCancel={() => setShowDeleteConfirm(false)}
          />
        )}
      </AnimatePresence>
      {removeTarget && (
        <ModalOverlay
          isDismissable={false}
          className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm"
        >
          <Modal className="fixed inset-0 flex items-center justify-center p-4">
            <Dialog role="alertdialog" className="w-full max-w-md rounded-xl border border-border bg-card shadow-xl">
              <DialogHeader>
                <DialogTitle>Remove image from product</DialogTitle>
                <DialogDescription>
                  This will unlink <span className="font-medium">{removeTarget.filename}</span> from this product.
                  The image will stay in Image Management.
                </DialogDescription>
              </DialogHeader>
              <DialogBody className="pt-0">
                <div className="flex items-center gap-3 rounded-lg border border-border/60 bg-muted/20 p-3 text-sm">
                  <img
                    src={removeTarget.url}
                    alt={removeTarget.filename}
                    className="h-12 w-12 rounded-md object-contain bg-background"
                  />
                  <div className="min-w-0">
                    <div className="truncate text-foreground">{removeTarget.filename}</div>
                    <div className="text-xs text-muted-foreground">SKU match: {removeTarget.matched_sku || '—'}</div>
                  </div>
                </div>
              </DialogBody>
              <DialogFooter>
                <Button intent="outline" size="sm" onPress={() => setRemoveTarget(null)}>
                  Cancel
                </Button>
                <Button
                  intent="danger"
                  size="sm"
                  onPress={async () => {
                    const target = removeTarget;
                    setRemoveTarget(null);
                    if (target) await handleRemoveFromProduct(target);
                  }}
                  isDisabled={removingImageId === removeTarget.id}
                >
                  {removingImageId === removeTarget.id ? 'Removing…' : 'Remove'}
                </Button>
              </DialogFooter>
            </Dialog>
          </Modal>
        </ModalOverlay>
      )}
    </>
  );
}

function DetailRow({
  label,
  editing,
  children,
}: {
  label: string;
  editing: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className={cn(
      'flex items-center justify-between px-3 py-2 transition-colors',
      editing && 'bg-primary/[0.02]'
    )}>
      <span className="text-xs text-muted-foreground/60 w-24 shrink-0">{label}</span>
      <div className="flex-1 text-right">{children}</div>
    </div>
  );
}
